import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/db/client";

/**
 * Keeps teams and their rosters fresh app-wide.
 *
 * Both queries are staleTime: Infinity, so this is the only thing that refreshes
 * them — a superadmin creating a team or moving somebody between teams has to
 * reach every open tab, or a colleague's team picker keeps offering a stale list.
 *
 * Events carry only {table, op, id}, so this invalidates rather than patching.
 * Subscribing to both tables is deliberate: 0018 adds a notify trigger to each,
 * and a trigger with no subscriber is dead weight in exactly the way #3408 was
 * a subscriber with no trigger.
 */
export const useGlobalTeamsSync = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({ queryKey: ["team-memberships"] });
      queryClient.invalidateQueries({ queryKey: ["my-teams"] });
    };

    const channel = supabase
      .channel("global-teams-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "teams" }, invalidate)
      .on("postgres_changes", { event: "*", schema: "public", table: "team_memberships" }, invalidate)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
};
